import { Platform } from 'react-native';
import type { TonyteTheme } from './types';

export const daylightTheme: TonyteTheme = {
  name: 'daylight',
  isDark: false,
  statusBarStyle: 'dark-content',
  colors: {
    background: '#F5F0E8',
    backgroundSecondary: '#EDE3D7',
    surface: '#F8F1E7',
    surfaceSoft: 'rgba(247,239,227,0.76)',
    surfaceElevated: '#FFFCF6',
    surfaceGlass: 'rgba(255,252,246,0.82)',
    textPrimary: '#181817',
    textSecondary: '#756D64',
    textMuted: '#A59C91',
    textPlaceholder: '#8B8379',
    bioText: '#5F5952',
    bioPlaceholder: '#8B8379',
    actionLabel: '#655F57',
    sectionHeading: '#3F3A35',
    chipText: '#5F5952',
    chipSelectedText: '#087E7D',
    border: 'rgba(148,126,102,0.16)',
    borderStrong: 'rgba(130,108,84,0.24)',
    cardBorder: 'rgba(148,126,102,0.18)',
    accent: '#F27D26',
    accentSecondary: '#159B9A',
    accentSecondarySoft: 'rgba(21,155,154,0.08)',
    accentSecondaryBorder: 'rgba(21,155,154,0.24)',
    inputBackground: 'rgba(255,250,242,0.86)',
    controlBackground: 'rgba(255,249,239,0.92)',
    chipBackground: 'rgba(250,243,232,0.86)',
    chipBackgroundActive: 'rgba(242,125,38,0.12)',
    filterActiveIcon: '#E56816',
    navBackground: 'rgba(246,237,224,0.95)',
    navIcon: '#70675E',
    navIconActive: '#E56816',
    cardBackground: 'rgba(255,252,246,0.84)',
    mediaActionBackground: 'rgba(255,249,239,0.92)',
    mediaActionBorder: 'rgba(148,126,102,0.22)',
    mediaActionIcon: '#3C3935',
    overlay: 'rgba(52,43,34,0.22)',
    divider: 'rgba(148,126,102,0.16)',
    icon: '#4E4A45',
    iconSubtle: '#6E6962',
    iconFaint: '#A39C93',
    iconMuted: '#918B83',
    success: '#168A55',
    error: '#C94747',
    warning: '#B87412',
  },
  gradients: {
    background: ['#EEF5F1', '#F5F0E8', '#EDE3D7'],
    surface: ['rgba(251,247,239,0.88)', 'rgba(246,238,226,0.86)', 'rgba(239,228,213,0.82)'],
    surfaceElevated: ['rgba(255,253,248,0.96)', 'rgba(252,246,236,0.91)', 'rgba(244,233,218,0.88)'],
    control: ['rgba(255,254,250,0.98)', 'rgba(251,244,233,0.96)', 'rgba(239,226,209,0.94)'],
    accent: ['#FFAA61', '#F27D26', '#C95812'],
    navBase: ['rgba(255,252,245,0.99)', 'rgba(245,235,220,0.985)', 'rgba(225,208,186,0.985)'],
    navAccent: ['rgba(255,255,255,0.42)', 'rgba(215,185,151,0.08)', 'rgba(123,87,52,0.035)'],
    navGlass: ['rgba(255,255,255,0.74)', 'rgba(255,249,239,0.22)', 'rgba(188,153,116,0.035)'],
    navWarm: ['rgba(255,240,217,0.04)', 'rgba(219,153,91,0.075)', 'rgba(126,87,53,0.065)'],
    navButtonActive: ['rgba(255,246,232,0.99)', 'rgba(247,225,199,0.99)', 'rgba(224,193,158,0.99)'],
    navButtonInactive: ['rgba(255,253,247,0.99)', 'rgba(245,235,221,0.99)', 'rgba(226,211,191,0.99)'],
  },
  shadows: {
    card: {
      shadowColor: '#8D745B', shadowOffset: { width: 0, height: 7 }, shadowOpacity: 0.10,
      shadowRadius: 16, elevation: 5,
    },
    elevated: {
      shadowColor: '#80684F', shadowOffset: { width: 0, height: 10 }, shadowOpacity: 0.13,
      shadowRadius: 23, elevation: 8,
    },
    navigation: {
      shadowColor: '#80684F', shadowOffset: { width: 0, height: -8 },
      shadowOpacity: Platform.OS === 'ios' ? 0.12 : 0.17,
      shadowRadius: Platform.OS === 'ios' ? 22 : 25, elevation: 12,
    },
  },
  eventCard: {
    shellBackground: '#F8F1E7',
    shellBorder: 'rgba(145,118,88,0.18)',
    edgeHighlight: 'rgba(255,255,255,0.72)',
    fallbackGradient: ['#DDECE7', '#EADFD0', '#D8C6B2'],
    imageOverlay: ['rgba(34,27,21,0.10)', 'rgba(42,31,22,0.025)', 'rgba(70,47,30,0.14)', 'rgba(55,38,27,0.54)'],
    colorWash: ['rgba(255,242,218,0.22)', 'rgba(242,125,38,0.10)', 'rgba(238,245,239,0)'],
    colorWashOpacity: 0.84,
    headerBackground: 'rgba(43,34,27,0.32)',
    headerBorder: 'rgba(255,248,238,0.24)',
    headerTitle: '#FFFDF8',
    headerMeta: 'rgba(255,250,242,0.90)',
    headerTextShadow: 'rgba(46,31,21,0.32)',
    categoryBackground: 'rgba(255,249,239,0.88)',
    categoryBorder: 'rgba(145,118,88,0.22)',
    categoryText: '#514B45',
    infoGradient: ['rgba(255,250,241,0.88)', 'rgba(245,235,220,0.80)', 'rgba(235,220,201,0.72)'],
    infoBorder: 'rgba(145,118,88,0.18)',
    infoPrimaryText: '#1C1A18',
    infoSecondaryText: '#6F665D',
    entryText: '#A84C16',
    chipBackground: 'rgba(255,249,239,0.86)',
    chipBorder: 'rgba(125,103,80,0.18)',
    chipText: '#514B45',
    bottomStatusText: 'rgba(255,248,238,0.88)',
    bottomMutedText: 'rgba(244,232,217,0.78)',
    manageBackground: 'rgba(255,249,239,0.90)',
    manageBorder: 'rgba(145,118,88,0.22)',
    manageText: '#3C3935',
    deleteBackground: 'rgba(255,242,231,0.90)',
    deleteBorder: 'rgba(190,91,62,0.24)',
    deleteText: '#B9503B',
  },
};
